import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  List,
  ListItemButton,
  Divider,
  useTheme, useMediaQuery
} from '@mui/material';
import { useSearchParams } from 'react-router-dom';

// Import sections
import OverviewSection from './WatchNodDocumentation/OverviewSection';
import DashboardSection from './WatchNodDocumentation/DashboardSection';
import NodeMonitorSection from './WatchNodDocumentation/NodeMonitorSection';
import WebsiteMonitorSection from './WatchNodDocumentation/WebsiteMonitorSection';
import NodeMetricsSection from './WatchNodDocumentation/NodeMetricsSection';
import WalletSection from './WatchNodDocumentation/WalletSection';
import SubscriptionSection from './WatchNodDocumentation/SubscriptionSection';
import ProfileSection from './WatchNodDocumentation/ProfileSection';
import ContactSalesSection from './WatchNodDocumentation/ContactSalesSection';
import AutoPaySection from './WatchNodDocumentation/AutoPaySection';
import DataRetentionPolicySection from './WatchNodDocumentation/DataRetentionPolicySection';
import UsagePolicySection from './WatchNodDocumentation/UsagePolicySection';

import IpAddressTypesSection from './WatchNodDocumentation/IpAddressTypesSection';
import NodeExporterDockerSection from './WatchNodDocumentation/NodeExporterDockerSetup';
import PushProxClientSection from './WatchNodDocumentation/PushProxClientSetup';
import WindowsExporterSection from './WatchNodDocumentation/WindowsExporterSetup';

const TabContent = ({ items, activeItemId, setActiveItemId }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        gap: isMobile ? 2 : 4,
        alignItems: isMobile ? 'stretch' : 'flex-start',
      }}
    >
      {/* Sidebar */}
      <Box
        sx={{
          width: isMobile ? '100%' : '240px',
          position: isMobile ? 'relative' : 'sticky',
          top: isMobile ? 'auto' : '100px',
          flexShrink: 0,
          overflowX: isMobile ? 'auto' : 'visible',
          whiteSpace: isMobile ? 'nowrap' : 'normal',
          borderBottom: isMobile ? `1px solid ${theme.palette.divider}` : 'none',
        }}
      >
        <List sx={{ display: isMobile ? 'flex' : 'block', p: 0 }}>
          {items.map((item) => (
            <ListItemButton
              key={item.id}
              selected={activeItemId === item.id}
              onClick={() => setActiveItemId(item.id)}
              sx={{
                fontSize: 16,
                borderRadius: 1,
                flex: isMobile ? '0 0 auto' : 'unset',
              }}
            >
              {item.label}
            </ListItemButton>
          ))}
        </List>
      </Box>

      {/* Main Content */}
      <Box sx={{ flexGrow: 1 }}>
        {items.map(
          (item) =>
            activeItemId === item.id && (
              <Box key={item.id} sx={{ mb: 6 }}>
                <Typography variant="h4" gutterBottom>
                  {item.label}
                </Typography>
                <Box>{item.content}</Box>
                <Divider sx={{ mt: 4 }} />
              </Box>
            )
        )}
      </Box>
    </Box>
  );
};

const DocumentDetail = () => {
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(0);
  const [activeItemOne, setActiveItemOne] = useState('item1');
  const [activeItemTwo, setActiveItemTwo] = useState('itemA');

  useEffect(() => {
    const tabParam = searchParams.get('tab');
    const sectionParam = searchParams.get('section');

    if (tabParam === '2') {
      setActiveTab(1);
      if (sectionParam) setActiveItemTwo(sectionParam);
    } else {
      setActiveTab(0);
      if (sectionParam) setActiveItemOne(sectionParam);
    }
  }, [searchParams]);

  useEffect(() => {
    const { hash } = window.location;
    if (hash) {
      const targetElement = document.querySelector(hash);
      if (targetElement) {
        targetElement.scrollIntoView({ behavior: 'smooth' });
      }
    }
  }, []);

  const tabOneItems = [
    { id: 'item1', label: 'Overview', content: <OverviewSection /> },
    { id: 'item2', label: 'Dashboard', content: <DashboardSection /> },
    { id: 'item3', label: 'Node Monitor Page', content: <NodeMonitorSection /> },
    { id: 'item4', label: 'API Monitor Page', content: <WebsiteMonitorSection /> },
    { id: 'item5', label: 'Node Metrics Page', content: <NodeMetricsSection /> },
    { id: 'item6', label: 'Wallet Page', content: <WalletSection /> },
    { id: 'item7', label: 'Subscription Page', content: <SubscriptionSection /> },
    { id: 'item8', label: 'Profile Page', content: <ProfileSection /> },
    { id: 'item9', label: 'Enterprise & Customize Plans', content: <ContactSalesSection /> },
    { id: 'item10', label: 'AutoPay Functionality', content: <AutoPaySection /> },
    { id: 'item11', label: 'Policy – Data Retention', content: <DataRetentionPolicySection /> },
    { id: 'item12', label: 'Usage Policy', content: <UsagePolicySection /> },
  ];

  const tabTwoItems = [
    {
      id: 'itemA',
      label: 'Types of IP Addresses and Their Functions',
      content: <IpAddressTypesSection />,
    },
    {
      id: 'itemB',
      label: 'Node Exporter with Docker For Linux/Mac',
      content: <NodeExporterDockerSection />,
    },
    {
      id: 'itemC',
      label: 'Windows Exporter with Docker For Windows',
      content: <WindowsExporterSection />,
    },
    {
      id: 'itemD',
      label: 'PushProx Client on Servers with Private IP using Docker For Linux/Mac',
      content: <PushProxClientSection />,
    },
  ];

  return (
    <Box sx={{ p: 4 }}>
      <Typography variant="h3" gutterBottom textAlign='center'>
        WatchNod Documentation
      </Typography>

      <Tabs
        value={activeTab}
        onChange={(e, newValue) => setActiveTab(newValue)}
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
        sx={{
          mb: 4,
          '& .MuiTab-root': {
            fontSize: '1.125rem',
            fontWeight: 600,
            textTransform: 'none', // keeps normal casing
            whiteSpace: 'nowrap',  // prevents wrapping
          }
        }}
      >
        <Tab label="Platform Overview" />
        <Tab label="Instruction Guide" />
      </Tabs>



      {activeTab === 0 && (
        <TabContent
          items={tabOneItems}
          activeItemId={activeItemOne}
          setActiveItemId={setActiveItemOne}
        />
      )}
      {activeTab === 1 && (
        <TabContent
          items={tabTwoItems}
          activeItemId={activeItemTwo}
          setActiveItemId={setActiveItemTwo}
        />
      )}
    </Box>
  );
};

export default DocumentDetail;
